import { useId, useState } from "react";

import { useBackgroundVocabularyQuery } from "@/api/backgroundVocabulary";
import type { LibraryAsset } from "@/api/library";
import { useCapability } from "@/auth/SessionContext";
import { lifecycleRefusal } from "@/lib/archiveRefusal";
import {
  BG_STRUCTURE_LANGUAGE,
  addItem,
  addQualityExample,
  addRule,
  addSafetyNet,
  anchorDocument,
  blankDescriptions,
  domainDocuments,
  moveItem,
  moveQualityExample,
  moveRule,
  effectiveSafetyNets,
  newItemIdRefusal,
  nextItemId,
  nextSafetyNetName,
  referenceProblems,
  removeItem,
  removeQualityExample,
  removeRule,
  removeSafetyNet,
  setDomainOpeningMode,
  setDomainOrder,
  setDomainText,
  setItemDescription,
  setItemFlag,
  setItemPatientInfoField,
  setItemProse,
  setItemType,
  setQualityExample,
  setRuleGate,
  setRuleList,
  setSafetyNetGateItem,
  setSafetyNetList,
  type BackgroundDomainDocument,
  type NewItemIdRefusal,
} from "@/lib/backgroundDomains";
import { PageHeader } from "@/shell/PageHeader";
import { Button } from "@/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/ui/Card";
import { FieldError, joinDescriptionIds } from "@/ui/FieldError";
import { Field, Input, Select, Textarea } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { Tooltip } from "@/ui/Tooltip";
import { SaveStatusLine } from "@/ui/library/AssetEditor";
import { AssetAuditTrail } from "@/ui/library/AssetAuditTrail";
import {
  LifecycleDialog,
  type LifecycleMode,
} from "@/ui/library/LifecycleDialog";
import { LoadingState, UnavailableState } from "@/ui/states";

import { BackgroundDomainRules } from "./BackgroundDomainRules";
import { BackgroundItemConditions } from "./BackgroundItemConditions";
import { BackgroundItemRows } from "./BackgroundItemRows";
import { NamedAssetMeta } from "./NamedAssetChrome";
import {
  NamedConflictRecovery,
  NamedProblems,
  NamedRefusal,
} from "./NamedDraftStates";
import { NamedReadFailureState } from "./NamedReadFailure";
import { NewBackgroundDomainDialog } from "./NewBackgroundDomainDialog";
import { useNamedDraft } from "./useNamedDraft";

/**
 * One independent BG domain, authored as named fields and ordered prompt rows
 * (#859).
 *
 * ## THE PREMISE THAT CHANGED, AND WHY THIS IS NOT A JSON EDITOR RETURNING
 *
 * Until #857 a `background_set` reached `AssetEditor`/`PartsEditor`: a free-text
 * "Part key" input above a textarea holding the raw `domains` document. That is
 * the state the ticket records — "the real BG screens show seeded read-only
 * JSON" — and `NamedDocumentEditor` explains at length why it could not simply
 * be replaced with a form: `StructuredPart.data` is an unconstrained
 * `JsonValue`, so a client typing the INSIDE of one would be deciding a shape
 * no authority in this repository publishes.
 *
 * #857 published it. `background_domains.py` is a write-time schema for exactly
 * these documents, derived from the pinned tracker's own vocabulary and pinned
 * to it by a real-checkout test: it names every admitted field, refuses an
 * unsupported one with a field pointer, refuses a rule naming an absent item,
 * and refuses an opening the runtime would never read. So the authority exists
 * now, and a typed surface over it is no longer this editor guessing — it is
 * this editor rendering the service's own contract. That is the whole reason
 * this page can exist and `NamedDocumentEditor`'s ruling still stands for every
 * structured document that has NOT been given one.
 *
 * ## WHAT IS EDITED HERE, AND WHAT #913 ADDS TO THE SAME ROWS
 *
 * The S7 contract splits this family across two leaves deliberately: "#859 owns
 * named domain/step editing; #913 adds the conditional/extraction/quality
 * controls to the same rows, not another editor". #859 authored the domain's
 * display name, its interview position, its opening and each item's prompt;
 * #913 fills the slot `BackgroundItemRows` published — each prompt's type,
 * gates, pre-fill key, extraction note and ordered quality examples — and adds
 * the `Domain rules` card below the prompts for the two collections that belong
 * to the DOMAIN rather than to any one prompt: the ordered conditional trigger
 * rules and the named safety nets.
 *
 * Every field of a source domain document is now authored here. What is still
 * preserved rather than authored is preserved the same way it always was:
 * `lib/backgroundDomains` mutates the parsed document in place and re-serialises
 * the same object, so nothing is rebuilt from the fields on screen.
 *
 * ## WHY A DELETED PROMPT DOES NOT REWRITE THE RULES THAT NAME IT
 *
 * #859 left a rule referencing a removed item alone on purpose — rewriting it
 * would be a client inventing a clinical decision — and the cost was that the
 * only feedback was a 422 pointing at `content.parts[0].data.
 * conditional_trigger_rules[3]`. `referenceProblems` closes that half:
 * every broken reference, in every stored language, is named before the request
 * and Save is held until it is fixed. The rule is still the service's; this is
 * only where the clinician is told which one to look at.
 *
 * ## THE ONE DIVERGENCE FROM THE REFERENCE SCREEN
 *
 * The reference's domain editor carries a `Back` button at the top right beside
 * the breadcrumb that already goes to the same place. This page renders the
 * breadcrumb alone: two controls with one destination is the duplicate-control
 * defect the shell's invariants forbid, and `MainConcernAssetPage` and
 * `PathwayAssetPage` already navigate up by crumb. Recorded here rather than
 * silently dropped.
 */
/**
 * Why a typed new-prompt id was refused, in the words a clinician reads (#977).
 *
 * The VERDICT is `lib/backgroundDomains.newItemIdRefusal`'s and the WORDING is
 * here, on the same split `backgroundVocabulary.ts` states for the tracker
 * statuses: a reason code is a fact about the document and the service's
 * grammar, an English sentence is a product decision, and a module every BG
 * edit path goes through should not have to change when the sentence does.
 *
 * `none` is a member so the mapping is TOTAL — `Record<…, string>` over the
 * union plus `none` means a reason added to the helper does not compile until it
 * has a sentence, which is the guard that stopped `AdoptFromLibraryAction`'s
 * lane map from silently rendering a blank.
 *
 * The shape sentence deliberately describes the grammar in words rather than
 * printing the regex: the pattern is the service's and may change, and a
 * clinician reading `[a-z][a-z0-9_]{0,63}` learns nothing they can act on. What
 * refuses is still the served pattern.
 */
const NEW_ITEM_ID_REFUSALS: Record<NewItemIdRefusal | "none", string | null> = {
  none: null,
  blank: "A new prompt needs an ID. It is what the tracker stores the answer under.",
  unreadable_policy:
    "The Editor Service has not told this screen which IDs it accepts, so this one cannot be checked.",
  shape:
    "The Editor Service does not accept this as a prompt ID. Use lowercase letters, digits and underscores, starting with a letter — for example pregnancy_and_birth.",
  duplicate:
    "This domain already has a prompt with that ID, in one of its stored languages. An ID identifies exactly one prompt.",
};

export function BackgroundDomainPage({ asset }: { asset: LibraryAsset }) {
  const state = useNamedDraft(asset);
  // THE SERVICE'S OWN ENUM VOCABULARY, read before the early returns below
  // because a hook cannot be called conditionally — and read at all because the
  // six tracker statuses are the PINNED ENGINE's, mirrored by
  // `background_domains.BackgroundItemStatus` and pinned to it by a
  // real-checkout test. A third copy in the browser would be free to offer an
  // operator the write gate refuses.
  const vocabulary = useBackgroundVocabularyQuery();
  /**
   * THE VOCABULARY READ HAS THREE STATES THIS CONTROL CARES ABOUT, not two —
   * `MainConcernAssetPage`'s `manifestUnread`, for the same reason and in the
   * same spelling (#977).
   *
   * `isError` is the SETTLED half only. A retry held by the retryer keeps
   * `status: "success"` with `isError: false` when a refresh of an
   * already-answered read is parked — the tab went to the background, or the
   * browser reports offline — so a grammar that will never arrive on its own
   * looks exactly like one that just did. `isSuccess` cannot see it either: a
   * PAUSED refresh over a cached response keeps the status `success`.
   *
   * RETAINED DATA DOES NOT EXCUSE THE READ. `itemIdPattern` survives both a
   * failed refresh and a paused one, so the pattern in hand is the last one the
   * service confirmed and the browser cannot say whether it is still that.
   * Creating against it is an assertion about service policy made by a client
   * whose last read of that policy did not complete — the drift
   * `NewBackgroundDomainDialog` refuses for a placement segment and
   * `BgSetAdoptionPicker` for a cardinality bound. Minting the permanent
   * identity of a runtime record is the last place to make an exception.
   *
   * A refresh that is merely IN FLIGHT is neither: it keeps `isSuccess` true
   * and both flags false, so nothing flickers off on a slow network.
   */
  const vocabularyUnread = vocabulary.isPaused || vocabulary.isError;
  const idGrammar = vocabularyUnread
    ? null
    : (vocabulary.data?.itemIdPattern ?? null);
  const write = useCapability("library.write");
  const restore = useCapability("library.restore");
  const [lifecycle, setLifecycle] = useState<LifecycleMode | null>(null);
  const [copying, setCopying] = useState(false);
  // Which stored language's prompts are on screen. `null` means "the anchor",
  // resolved below rather than seeded into state, so a draft re-seeded by
  // somebody else's publish cannot leave this pointing at a document that is
  // no longer there.
  const [promptLanguage, setPromptLanguage] = useState<string | null>(null);
  // THE RAW INTERVIEW-POSITION TEXT, while it is not yet an integer.
  //
  // `null` means "show what the document declares". A number input legitimately
  // passes through values that are not the position the clinician is heading
  // for — `""` on the way to `12`, `2.` on the way to `2.5`, `-` on the way to
  // `-1` — and the draft may not be written from any of them. Without this the
  // field could not even be CLEARED: it renders from the draft, so an empty box
  // re-rendered instantly with the old value.
  //
  // HELD WITH THE PIN IT WAS TYPED AGAINST, so staleness is DERIVED rather than
  // synchronised. Save and "edit the newer version instead" both re-pin, and
  // what is on screen afterwards is the SERVICE's document — a half-typed `2.`
  // left over from before must not be presented as that document's position.
  // Comparing the recorded pin below drops it exactly then, with no effect to
  // reset it (`react-hooks/set-state-in-effect`, and the render-time seeding
  // `useNamedDraft` uses for the same reason).
  const [orderInput, setOrderInput] = useState<{
    pinned: string;
    text: string;
  } | null>(null);
  // A NEW prompt id is not document data until Add is pressed, but it still
  // belongs to the draft/version being edited. Holding the pin beside the text
  // makes Save, rebase and "edit the newer version" drop stale staging by
  // derivation; the page's Discard handler clears the same buffer explicitly.
  const [newItemIdInput, setNewItemIdInput] = useState<{
    pinned: string;
    text: string;
  } | null>(null);
  const orderErrorId = useId();

  const breadcrumbs = [
    { label: "Clinical library", to: "/library" },
    { label: "BG sets", to: "/library/bg-sets" },
  ];

  if (state.status === "loading") {
    return (
      <>
        <PageHeader breadcrumbs={breadcrumbs} title={asset.name} />
        <LoadingState label="Loading domain content" rows={4} />
      </>
    );
  }

  if (state.status === "error") {
    return (
      <>
        <PageHeader breadcrumbs={breadcrumbs} title={asset.name} />
        <NamedReadFailureState
          failure={state.failure}
          isPaused={state.isPaused}
          retry={state.retry}
          subject="Domain content"
          title="This domain's content could not be loaded"
          description={`The Editor Service did not return the content at ${asset.latestVersion}, so there is nothing to edit yet.`}
          deniedDescription="You are not authorized to read this domain's content."
          offlineDescription={`The content at ${asset.latestVersion} could not be reached, so there is nothing to edit yet. It loads on its own when the connection returns.`}
        />
      </>
    );
  }

  if (state.status === "unsupported") {
    return (
      <>
        <PageHeader breadcrumbs={breadcrumbs} title={asset.name} />
        <UnavailableState
          title="This asset's content is not a set of documents"
          description="A BG domain is authored as one structured document per language, and this asset's stored content is a different shape."
        />
      </>
    );
  }

  const { editor } = state;
  const orderText =
    orderInput && orderInput.pinned === editor.pinned ? orderInput.text : null;
  const newItemIdText =
    newItemIdInput && newItemIdInput.pinned === editor.pinned
      ? newItemIdInput.text
      : null;
  const documents = domainDocuments(editor.parts);
  const anchor = anchorDocument(documents);
  const view = anchor?.view ?? null;

  const archiveMode: LifecycleMode = asset.capabilities.archived
    ? "unarchive"
    : "archive";
  // The direction-specific caller gate (#647): archiving is `library.write`,
  // restoring is the admin/operations `library.restore` the ratified contract
  // reserves, matching the two routes the service gates separately.
  const archiveRefusal = lifecycleRefusal(
    asset,
    archiveMode === "unarchive" ? restore.reason : write.reason,
  );
  const archiveLabel = archiveMode === "archive" ? "Archive" : "Unarchive";

  const header = (
    <PageHeader
      breadcrumbs={breadcrumbs}
      title={asset.name}
      description={
        view
          ? `${view.items.length} ${view.items.length === 1 ? "prompt" : "prompts"}`
          : "Background domain"
      }
      meta={
        <NamedAssetMeta
          asset={asset}
          version={editor.pinned}
          identity={`bg-sets/${asset.id}`}
        />
      }
      actions={
        <>
          {asset.capabilities.seeded ? (
            <Tooltip content="Part of the base clinical library. The original stays as it is — make an editable copy to create your own version.">
              <Button variant="outline" onClick={() => setCopying(true)}>
                Create editable copy
              </Button>
            </Tooltip>
          ) : null}
          <Tooltip content={archiveRefusal ?? `${archiveLabel} this asset`}>
            <Button
              variant="outline"
              disabled={archiveRefusal !== null}
              onClick={() => setLifecycle(archiveMode)}
            >
              {archiveLabel}
            </Button>
          </Tooltip>
        </>
      }
    />
  );

  // A version whose `domains` documents this build cannot project — an
  // unparseable buffer, or a set carrying two non-anchor languages with no `he`
  // to anchor on. The stored content is still readable through the version
  // history below, so the page keeps its chrome and states the limit rather
  // than offering a form over something it did not understand.
  if (anchor === null || view === null) {
    return (
      <>
        {header}
        <div className="space-y-6">
          <UnavailableState
            title="This version's domain document could not be read"
            description={
              documents.length === 0
                ? "This version carries no background domain document, so there is nothing to author. Its stored content is below."
                : `This version's domain documents could not be read as one domain — the runtime reads a domain's structure from its "${BG_STRUCTURE_LANGUAGE}" document. The stored content is below, unchanged.`
            }
          />
          <AssetAuditTrail assetId={asset.id} />
        </div>
      </>
    );
  }

  // The document whose prompt text is on screen: the chosen language, or the
  // anchor. Falls back to the anchor whenever the chosen one is gone or cannot
  // be read, so a selection can never leave the list empty.
  const chosen = documents.find(
    (document) => document.language === promptLanguage && document.view !== null,
  );
  const shown = chosen ?? anchor;
  const shownView = chosen?.view ?? view;

  // WHAT THE BOX HOLDS THAT THE DOCUMENT DOES NOT, said rather than swallowed.
  // The draft keeps the last integer, so a clinician who typed `2.5` would
  // otherwise see their own text beside an unchanged, silently different stored
  // position. `null` while the text is absent or already an integer.
  //
  // BLANK IS TESTED FIRST, and that ordering is the point rather than tidiness:
  // `Number("")` is 0, so a blank box passes `Number.isInteger` and would have
  // reported itself as applied while the document kept its old position — the
  // same silent divergence in the other direction.
  const orderRefusal =
    orderText === null
      ? null
      : orderText.trim() === ""
        ? `This domain still sits at position ${view.order ?? "—"}. Type a whole number to move it.`
        : Number.isInteger(Number(orderText))
          ? null
          : "An interview position is a whole number, so this value has not been applied.";

  // Prompts still blank in some stored language. Adding a prompt writes it into
  // every language, and only one is on screen, so this is the difference
  // between "Save is refused because English is empty" and a 422 pointing at a
  // document the author never saw.
  const blanks = blankDescriptions(editor.parts);
  const blankRefusal =
    blanks.length === 0
      ? null
      : `Every prompt needs text in each stored language before this domain can be saved: ${blanks
          .map((blank) => `${blank.itemId} (${blank.languages.join(", ")})`)
          .join("; ")}.`;
  // A REFUSED POSITION ALSO REFUSES THE SAVE, and this is the residue of the
  // same bug rather than an extra rule. Typing `2.5` passes through `2`, which
  // IS an integer and is therefore applied — so without this the clinician
  // would be looking at `2.5`, at a sentence saying it was not applied, and at
  // an enabled Save that would publish `2`. The value that would be written
  // must never be one that is neither on screen nor previously stored.
  // BROKEN REFERENCES, in every stored language. Deleting a prompt is the one
  // edit here that can break a rule the clinician is not looking at — in a
  // document that may not even be on screen — so the sentence names the rule and
  // the prompt it wanted rather than leaving a 422's field pointer to do it.
  const broken = referenceProblems(editor.parts);
  const referenceRefusal =
    broken.length === 0
      ? null
      : `Every rule has to name a prompt this domain still has: ${broken
          .map(
            (problem) =>
              `${problem.where} (${problem.language}) names ${
                problem.missingId === ""
                  ? "no prompt at all"
                  : `"${problem.missingId}", which is gone`
              }`,
          )
          .join("; ")}.`;
  const saveRefusal = orderRefusal ?? blankRefusal ?? referenceRefusal;

  // WHAT DISCARD HAS TO PUT BACK, which is not the same set as what Save would
  // publish.
  //
  // A refused position leaves the DOCUMENT untouched — that is the whole point
  // of refusing it — so `editor.dirty` stays false and a Discard gated on it
  // alone was disabled exactly when the clinician most needed it: the box holds
  // text the document does not, a refusal says so, and the only way back to the
  // stored value was to retype it from memory.
  //
  // Two ways in, and the second is not obvious: clearing the field (nothing is
  // ever committed), and a value whose last VALID intermediate equals the
  // stored position — typing `4.5` over a stored `4` commits `4` on the first
  // keystroke, which is no change at all, and then refuses the rest.
  const canonicalOrderText = view.order === null ? "" : String(view.order);
  const orderTextDiverges =
    orderText !== null && orderText !== canonicalOrderText;
  const suggestedItemId = nextItemId(editor.parts);
  const newItemIdTextDiverges =
    newItemIdText !== null && newItemIdText !== suggestedItemId;
  const discardable =
    editor.dirty || orderTextDiverges || newItemIdTextDiverges;

  const saveControls = editor.readOnly ? null : (
    <div className="flex flex-wrap items-center gap-3">
      <SaveStatusLine
        status={
          editor.saving
            ? "saving"
            : editor.failure !== null
              ? "error"
              : editor.dirty
                ? "dirty"
                : editor.lastSavedAt
                  ? "saved"
                  : "clean"
        }
        lastSavedAt={editor.lastSavedAt}
      />
      <Button
        variant="outline"
        size="sm"
        disabled={!discardable || editor.saving}
        onClick={() => {
          // Both halves, in one press. `editor.discard()` on an already-clean
          // draft is a no-op, so the raw-text-only case does not need its own
          // control or its own branch.
          setOrderInput(null);
          setNewItemIdInput(null);
          editor.discard();
        }}
      >
        Discard
      </Button>
      <Tooltip content={saveRefusal ?? "Publish these changes as a new version"}>
        <Button
          size="sm"
          disabled={!editor.dirty || editor.saving || saveRefusal !== null}
          onClick={() => editor.save()}
        >
          Save
        </Button>
      </Tooltip>
    </div>
  );

  return (
    <>
      {header}

      <div className="space-y-6">
        <NamedConflictRecovery editor={editor} />
        <NamedRefusal editor={editor} />
        <NamedProblems editor={editor} />

        {editor.readOnly && editor.readOnlyReason ? (
          <Notice>{editor.readOnlyReason}</Notice>
        ) : null}

        {/* `@container` on the card, the idiom `LibraryCatalog` uses: the
            `@2xl:` grids inside measure their CONTAINER, and without one
            declared they measured nothing and never applied — every two-column
            row rendered stacked at every width. */}
        <Card className="@container">
          <CardHeader>
            <CardTitle>Domain</CardTitle>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="grid gap-4 @2xl:grid-cols-2">
              {/*
                THE ID IS IDENTITY, NEVER A CONTROL. It is the file stem the
                pinned engine reads back (`list_variant_categories`), the key
                the project roster names, and the last segment of the asset's
                placement coordinate — three things that must agree, which is
                why `validate_background_domains` refuses a document whose
                `domain` is not its coordinate's slug. Renaming it after v1
                would silently orphan every project pinning this asset, so the
                value is set once at creation and shown here as a fact.
              */}
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">Domain ID</p>
                <code
                  dir="ltr"
                  className="block rounded bg-muted px-2 py-1.5 font-mono text-sm text-foreground"
                >
                  {view.domain}
                </code>
                <p className="text-xs text-muted-foreground">
                  The runtime identity of this domain. Set when the domain is
                  created and never renamed.
                  {asset.placementCoordinate ? (
                    <>
                      {" "}
                      Pack placement{" "}
                      <code dir="ltr" className="font-mono text-foreground">
                        {asset.placementCoordinate}
                      </code>
                      .
                    </>
                  ) : null}
                </p>
              </div>
              {/* THE PACK PLACEMENT, read-only, as the service states it. The
                  generic asset page rendered this for every family and this
                  page replaced that chrome for BG sets, so it is carried over
                  rather than dropped: a clinician reconciling a project's
                  runtime roster against the library needs the coordinate, and
                  it is the one fact that distinguishes two domains sharing a
                  display name across profile variants. */}
              <Field
                label="Interview position"
                description="Where this domain sits in the background interview. Every project pinning it reads the same position, and two pinned domains cannot share one."
              >
                {({ inputId, describedBy }) => (
                  <>
                    <Input
                      id={inputId}
                      type="number"
                      step={1}
                      inputMode="numeric"
                      aria-invalid={orderRefusal !== null || undefined}
                      aria-describedby={joinDescriptionIds(
                        describedBy,
                        orderRefusal ? orderErrorId : undefined,
                      )}
                      value={orderText ?? canonicalOrderText}
                      readOnly={editor.readOnly}
                      onChange={(event) => {
                        setOrderInput({
                          pinned: editor.pinned,
                          text: event.currentTarget.value,
                        });
                        // `valueAsNumber`, NEVER `parseInt`. A number input
                        // accepts syntax `parseInt` reads a DIFFERENT value
                        // out of: it stops at the first character it cannot
                        // use, so `2.5` becomes 2 and `1e2` becomes 1 — and
                        // both are integers, so the guard passed and the wrong
                        // position was written silently. The runtime sorts the
                        // background interview on this field and two pinned
                        // domains may not share a position, so a silently
                        // altered value reorders a clinical interview.
                        //
                        // `valueAsNumber` is the value the CONTROL holds
                        // (`2.5`, `100`, `NaN` for an empty or unparseable
                        // box), so `Number.isInteger` then means what it says.
                        const next = event.currentTarget.valueAsNumber;
                        if (Number.isInteger(next)) {
                          editor.change(setDomainOrder(editor.parts, next));
                        }
                      }}
                    />
                    {orderRefusal ? (
                      <FieldError id={orderErrorId} message={orderRefusal} />
                    ) : null}
                  </>
                )}
              </Field>
            </div>

            <Field
              label="Opening"
              description="Which opening the runtime reads for this domain. The text moves with the choice, so nothing already written is lost."
            >
              {({ inputId, describedBy }) => (
                <Select
                  id={inputId}
                  aria-describedby={describedBy}
                  value={view.hasOpeningQuestion ? "opening" : "question"}
                  disabled={editor.readOnly}
                  onChange={(event) =>
                    editor.change(
                      setDomainOpeningMode(
                        editor.parts,
                        event.currentTarget.value === "opening",
                      ),
                    )
                  }
                >
                  <option value="opening">
                    Opening prompt — the domain introduces itself
                  </option>
                  <option value="question">
                    First question — the domain opens by asking
                  </option>
                </Select>
              )}
            </Field>

            {/* ONE FIELD PER STORED DOCUMENT, `NamedDocumentEditor`'s rule: a
                monolingual asset renders exactly one box, and an asset stored
                in Hebrew and English renders one per language rather than
                picking one and hiding the other. */}
            {documents.map((document) => (
              <DomainTextFields
                key={document.documentId}
                document={document}
                showLanguage={documents.length > 1}
                readOnly={editor.readOnly}
                onChange={(field, value) =>
                  editor.change(
                    setDomainText(
                      editor.parts,
                      document.documentId,
                      field,
                      value,
                    ),
                  )
                }
              />
            ))}
          </CardBody>
        </Card>

        <Card className="@container">
          {/* The reference puts Discard and Save at the top right of the Steps
              card, beside the count — not in a footer under the rows. */}
          <CardHeader className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <CardTitle>Prompts</CardTitle>
            {saveControls}
          </CardHeader>
          <CardBody className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Prompts run in interview order. Each one is the text the runtime
              works from for a single tracked item; its identity, conditions and
              extraction settings stay with the item.
            </p>

            {/* A REFUSAL IN A TOOLTIP IS NOT A REPORTED FAILURE — the same rule
                `MainConcernAssetPage` states for its manifest read. The tooltip
                answers someone who hovers the disabled Save; the fact that a
                prompt is blank in a language not on screen needs somewhere it
                is simply visible, beside the list it is about.

                THE PROMPT HALF ONLY. A refused interview position also refuses
                Save, but it is already stated at the field it belongs to — and
                one sentence rendered in two places is two alerts for one fact,
                which is the duplicate-message defect rather than emphasis. */}
            {blankRefusal ? <Notice role="alert">{blankRefusal}</Notice> : null}
            {/* THE SAME RULE, for the same reason, about the other half of the
                document. A broken reference is stated beside the prompts
                because deleting one is what breaks it — and the rule that now
                names nothing is in the card below, which may be scrolled off
                screen entirely. */}
            {referenceRefusal ? (
              <Notice role="alert">{referenceRefusal}</Notice>
            ) : null}

            {/*
              ONE LANGUAGE'S PROMPTS AT A TIME, and only when there is more than
              one to choose from — `NamedDocumentEditor`'s rule, applied to a
              list rather than to a single field. A monolingual domain renders
              no control at all; a bilingual one would otherwise show twelve
              boxes for six prompts, and showing only the anchor's would leave
              the other language's prose silently unauthored.

              ORDER, ADD AND REMOVE ARE NOT PER LANGUAGE: they change the
              interview itself, so they apply to every stored document (see
              `lib/backgroundDomains`). Only the TEXT belongs to the language
              selected here.
            */}
            {documents.length > 1 ? (
              <Field
                label="Prompt language"
                description="Which stored language's prompt text is shown. Reordering, adding and removing a prompt applies to every language."
              >
                {({ inputId, describedBy }) => (
                  <Select
                    id={inputId}
                    aria-describedby={describedBy}
                    value={shown.language ?? ""}
                    onChange={(event) =>
                      setPromptLanguage(event.currentTarget.value || null)
                    }
                  >
                    {documents.map((document) => (
                      <option
                        key={document.documentId}
                        value={document.language ?? ""}
                      >
                        {document.language ?? "All languages"}
                        {document.language === BG_STRUCTURE_LANGUAGE
                          ? " — the language the runtime reads this domain's structure from"
                          : ""}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            ) : null}

            <BackgroundItemRows
              items={shownView.items}
              language={shown.language}
              readOnly={editor.readOnly}
              readOnlyReason={editor.readOnlyReason}
              onChangeDescription={(itemId, value) =>
                editor.change(
                  setItemDescription(
                    editor.parts,
                    shown.documentId,
                    itemId,
                    value,
                  ),
                )
              }
              onMove={(itemId, to) =>
                editor.change(moveItem(editor.parts, itemId, to))
              }
              onRemove={(itemId) =>
                editor.change(removeItem(editor.parts, itemId))
              }
              add={{
                label: "Add prompt",
                // AN UNREAD GRAMMAR IS NOT A PERMISSIVE ONE (#977), and
                // `idGrammar` above is what makes "unread" include a read that
                // has since been refused. `newItemIdRefusal` answers
                // `unreadable_policy` for the same `null`, so the control and
                // the field agree by construction rather than by two
                // conditions that can drift apart.
                // UNREAD IS TESTED FIRST, which is #327's ordering rather than
                // a preference: a paused read is `isPending` too, and testing
                // pending first would tell a clinician the grammar is seconds
                // away when nothing is on its way to arrive.
                refusal: vocabularyUnread
                  ? "The Editor Service did not return the IDs it accepts for a new prompt, so no prompt can be added. Everything already here is unchanged."
                  : idGrammar === null
                    ? "Loading the IDs the Editor Service accepts. A prompt can be added once they arrive."
                    : null,
                suggestedId: suggestedItemId,
                typedId: newItemIdText,
                idRefusal: (itemId) =>
                  NEW_ITEM_ID_REFUSALS[
                    newItemIdRefusal(editor.parts, itemId, idGrammar) ?? "none"
                  ],
                onChangeTypedId: (text) =>
                  setNewItemIdInput({ pinned: editor.pinned, text }),
                onAdd: (itemId) => {
                  editor.change(addItem(editor.parts, itemId));
                  // Follow the NEXT generated suggestion after this id is used.
                  setNewItemIdInput(null);
                },
              }}
              // #913's controls, inside the row they belong to. The render prop
              // is called per item and keyed by `item.id` upstream, so a reorder
              // moves the prompt and its conditions together.
              // THE ANCHOR'S ITEM, not the one whose prompt text is on screen.
              // Everything this expansion authors is read by the runtime from
              // the anchor document (`_merge_lang_configs` replaces only the
              // description per language), so showing the selected language's
              // copy would show values no runtime reads and no control writes.
              itemExtension={(item) => (
                <BackgroundItemConditions
                  item={
                    view.items.find((held) => held.id === item.id) ?? item
                  }
                  language={anchor.language}
                  shownLanguage={
                    shown.documentId === anchor.documentId
                      ? null
                      : (shown.language ?? "all languages")
                  }
                  itemTypes={vocabulary.data?.itemTypes ?? []}
                  readOnly={editor.readOnly}
                  onChangeType={(value) =>
                    editor.change(setItemType(editor.parts, item.id, value))
                  }
                  onChangePatientInfoField={(value) =>
                    editor.change(
                      setItemPatientInfoField(editor.parts, item.id, value),
                    )
                  }
                  onChangeFlag={(field, value) =>
                    editor.change(
                      setItemFlag(editor.parts, item.id, field, value),
                    )
                  }
                  onChangeProse={(field, value) =>
                    editor.change(
                      setItemProse(editor.parts, item.id, field, value),
                    )
                  }
                  onChangeExample={(index, field, value) =>
                    editor.change(
                      setQualityExample(
                        editor.parts,
                        item.id,
                        index,
                        field,
                        value,
                      ),
                    )
                  }
                  onAddExample={() =>
                    editor.change(addQualityExample(editor.parts, item.id))
                  }
                  onRemoveExample={(index) =>
                    editor.change(
                      removeQualityExample(editor.parts, item.id, index),
                    )
                  }
                  onMoveExample={(index, to) =>
                    editor.change(
                      moveQualityExample(editor.parts, item.id, index, to),
                    )
                  }
                />
              )}
            />
          </CardBody>
        </Card>

        {/* THE DOMAIN'S OWN RULES, under the prompts they reference and above
            the audit trail. A separate card rather than a third disclosure
            inside a row, because neither collection belongs to any single
            prompt: a rule reads one prompt and writes several, and moving one
            prompt must not move a rule with it. */}
        <Card className="@container">
          <CardHeader>
            <CardTitle>Domain rules</CardTitle>
          </CardHeader>
          <CardBody>
            <BackgroundDomainRules
              // THE ANCHOR'S ITEMS ARE THE REFERENCE VOCABULARY: adding,
              // removing and reordering a prompt applies to every language, so
              // the sets agree, and the anchor is the one the runtime resolves
              // a rule against.
              items={view.items}
              // ANCHOR-OWNED. `_merge_lang_configs` takes the rule list from
              // `dict(he_cfg)` and never looks at the other language's.
              rules={view.conditionalTriggerRules}
              // THE MERGED SET THE RUNTIME READS, not one document's table.
              // The engine unions the trigger names across languages and falls
              // back to the other language for a name the active one does not
              // carry, so rendering the shown document's table alone hid nets
              // that were firing and made a deletion look like one when it was
              // a reversion.
              //
              // COMPUTED FOR THE ANCHOR LANGUAGE, which is the conversation
              // language of every project that can pin this domain today
              // (`supported_languages: [he]`), and the fallback source for any
              // other. Each row says where its entry actually lives.
              safetyNets={effectiveSafetyNets(
                editor.parts,
                anchor.language ?? BG_STRUCTURE_LANGUAGE,
              )}
              safetyNetLanguage={anchor.language}
              statuses={vocabulary.data?.itemStatuses ?? []}
              // THE SERVICE'S OWN ANSWER, not the array's length: an empty list
              // is also what a request in flight and a failed one look like, and
              // neither licenses calling a stored status invalid.
              vocabularyResolved={vocabulary.data !== undefined}
              vocabularyRefusal={
                vocabulary.data
                  ? null
                  : vocabulary.isPending
                    ? "Loading the statuses a rule may name. Stored rules are shown; the status choices arrive with them."
                    : "The Editor Service did not return the statuses a rule may name, so no status choice is offered. Every stored rule is shown and preserved exactly as it is."
              }
              language={anchor.language}
              readOnly={editor.readOnly}
              onAddRule={() =>
                editor.change(
                  addRule(
                    editor.parts,
                    view.items[0]?.id ?? "",
                    // `captured` is the status the source's own rules key on
                    // most, and the seed has to BE one: `equals` is validated
                    // against the tracker's enum, so an empty string would make
                    // a new rule a document the service refuses on sight.
                    vocabulary.data?.itemStatuses.includes("captured")
                      ? "captured"
                      : (vocabulary.data?.itemStatuses[0] ?? "captured"),
                  ),
                )
              }
              onRemoveRule={(index) =>
                editor.change(removeRule(editor.parts, index))
              }
              onMoveRule={(index, to) =>
                editor.change(moveRule(editor.parts, index, to))
              }
              onChangeRuleGate={(index, field, value) =>
                editor.change(setRuleGate(editor.parts, index, field, value))
              }
              onChangeRuleList={(index, field, values) =>
                editor.change(setRuleList(editor.parts, index, field, values))
              }
              onAddSafetyNet={() =>
                editor.change(
                  addSafetyNet(
                    editor.parts,
                    nextSafetyNetName(editor.parts),
                    view.items[0]?.id ?? "",
                    // A `_`-prefixed net names no prompt of its own, so the
                    // service requires at least one target. Seeding the first
                    // prompt is what makes a new net a document that can be
                    // saved before it is finished being written.
                    view.items[0] ? [view.items[0].id] : [],
                  ),
                )
              }
              onRemoveSafetyNet={(name) =>
                editor.change(removeSafetyNet(editor.parts, name))
              }
              // ADDRESSED BY THE DOCUMENT THE ENTRY LIVES IN, which for an
              // inherited net is another language's: editing it edits the copy
              // the runtime is reading rather than minting a second one that
              // would then shadow it.
              onChangeSafetyNetGateItem={(documentId, name, value) =>
                editor.change(
                  setSafetyNetGateItem(editor.parts, documentId, name, value),
                )
              }
              onChangeSafetyNetList={(documentId, name, field, values) =>
                editor.change(
                  setSafetyNetList(
                    editor.parts,
                    documentId,
                    name,
                    field,
                    values,
                  ),
                )
              }
            />
          </CardBody>
        </Card>

        <AssetAuditTrail assetId={asset.id} />
      </div>

      {lifecycle ? (
        <LifecycleDialog
          asset={asset}
          mode={lifecycle}
          onClose={() => setLifecycle(null)}
        />
      ) : null}
      {copying ? (
        <NewBackgroundDomainDialog
          copyFrom={asset}
          onClose={() => setCopying(false)}
        />
      ) : null}
    </>
  );
}

/**
 * The display name and opening text of ONE stored language document.
 *
 * Both are per-language by construction: `_merge_lang_configs` builds a domain
 * from the anchor and replaces the display fields with the requested language's,
 * so a Hebrew `display_name` and an English one are two real values rather than
 * one translated at read time. The structural fields — order, the opening MODE,
 * the item list — are edited once for every language, which is why they are not
 * here.
 */
function DomainTextFields({
  document,
  showLanguage,
  readOnly,
  onChange,
}: {
  document: BackgroundDomainDocument;
  showLanguage: boolean;
  readOnly: boolean;
  onChange: (
    field: "display_name" | "opening_prompt" | "first_question",
    value: string,
  ) => void;
}) {
  const view = document.view;
  if (view === null) {
    return (
      <Notice>
        One of this version&apos;s domain documents could not be read, so its
        text is not shown here. It is preserved exactly as stored.
      </Notice>
    );
  }
  const suffix = showLanguage ? ` (${document.language ?? "all languages"})` : "";
  const openingLabel =
    view.openingField === "opening_prompt" ? "Opening prompt" : "First question";

  return (
    <div className="space-y-4">
      <Field
        label={`Display name${suffix}`}
        description="What a clinician reads for this domain. Separate from the runtime identity above."
      >
        {({ inputId, describedBy }) => (
          <Input
            id={inputId}
            aria-describedby={describedBy}
            value={view.displayName}
            readOnly={readOnly}
            dir="auto"
            {...(document.language ? { lang: document.language } : {})}
            onChange={(event) =>
              onChange("display_name", event.currentTarget.value)
            }
          />
        )}
      </Field>

      <Field
        label={`${openingLabel}${suffix}`}
        description={
          view.openingField === "opening_prompt"
            ? "How the runtime introduces this domain before its first prompt."
            : "The question the runtime opens this domain with."
        }
      >
        {({ inputId, describedBy }) => (
          <Textarea
            id={inputId}
            rows={3}
            aria-describedby={describedBy}
            value={view.openingText}
            readOnly={readOnly}
            dir="auto"
            {...(document.language ? { lang: document.language } : {})}
            onChange={(event) =>
              onChange(view.openingField, event.currentTarget.value)
            }
          />
        )}
      </Field>
    </div>
  );
}
